import { useCategories } from "#hooks/use-categories"
import { useI18n } from "#hooks/use-i18n"
import { getCategoryBackgroundStyle } from "#lib/category-background"
import type { ExpenseFilters } from "@home-finance-manager/shared/types/expenses"
import { Search, X } from "lucide-react"
import React, { useEffect, useMemo, useState } from "react"
import { Button } from "../ui/button"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"

interface ExpenseFiltersProps {
  filters: ExpenseFilters
  onFiltersChange: (filters: ExpenseFilters) => void
  onApply?: () => void
  onReset?: () => void
}

const ExpenseFilters: React.FC<ExpenseFiltersProps> = ({
  filters,
  onFiltersChange,
  onApply,
  onReset,
}) => {
  const { t } = useI18n()
  const { data: categories = [] } = useCategories()

  const [localFilters, setLocalFilters] = useState<ExpenseFilters>(filters)
  const [localSearch, setLocalSearch] = useState(filters.search || "")

  useEffect(() => {
    setLocalFilters(filters)
    setLocalSearch(filters.search || "")
  }, [filters])

  const handleSearchChange = (value: string) => {
    setLocalSearch(value)
    setLocalFilters((prev) => ({
      ...prev,
      search: value.trim() || undefined,
    }))
  }

  const handleCategoryToggle = (categoryId: number) => {
    const currentIds = localFilters.categoryIds || []
    const newIds = currentIds.includes(categoryId)
      ? currentIds.filter((id) => id !== categoryId)
      : [...currentIds, categoryId]
    setLocalFilters({
      ...localFilters,
      categoryIds: newIds.length > 0 ? newIds : undefined,
    })
  }

  const handleRecurringToggle = (recurringType: string) => {
    const currentTypes = localFilters.recurringTypes || []
    const newTypes = currentTypes.includes(recurringType)
      ? currentTypes.filter((type) => type !== recurringType)
      : [...currentTypes, recurringType]
    setLocalFilters({
      ...localFilters,
      recurringTypes: newTypes.length > 0 ? newTypes : undefined,
    })
  }

  const handleSelectAllCategories = () => {
    const allCategoryIds = categories.map((cat) => cat.id)
    setLocalFilters({
      ...localFilters,
      categoryIds: allCategoryIds,
    })
  }

  const handleDeselectAllCategories = () => {
    setLocalFilters({
      ...localFilters,
      categoryIds: undefined,
    })
  }

  const handleSelectAllRecurring = () => {
    const allRecurringTypes = ["one-time", "monthly", "quarterly", "yearly"]
    setLocalFilters({
      ...localFilters,
      recurringTypes: allRecurringTypes,
    })
  }

  const handleDeselectAllRecurring = () => {
    setLocalFilters({
      ...localFilters,
      recurringTypes: undefined,
    })
  }

  // Check if all categories are selected
  const allCategoriesSelected = useMemo(() => {
    if (categories.length === 0) return false
    const selectedIds = localFilters.categoryIds || []
    return (
      selectedIds.length === categories.length &&
      categories.every((cat) => selectedIds.includes(cat.id))
    )
  }, [categories, localFilters.categoryIds])

  // Check if all recurring types are selected
  const allRecurringSelected = useMemo(() => {
    const allRecurringTypes = ["one-time", "monthly", "quarterly", "yearly"]
    const selectedTypes = localFilters.recurringTypes || []
    return (
      selectedTypes.length === allRecurringTypes.length &&
      allRecurringTypes.every((type) => selectedTypes.includes(type))
    )
  }, [localFilters.recurringTypes])

  const handleIsVirtualChange = (value: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      isVirtual: value === "all" ? undefined : value,
    }))
  }

  const handleApply = () => {
    // Apply the filters first
    onFiltersChange(localFilters)
    // Close the popover/drawer after a brief delay to ensure state updates
    setTimeout(() => {
      onApply?.()
    }, 0)
  }

  const handleReset = () => {
    const emptyFilters: ExpenseFilters = {}
    // Reset local state first
    setLocalFilters(emptyFilters)
    setLocalSearch("")
    // Apply empty filters immediately to update URL
    onFiltersChange(emptyFilters)
    // Close the popover/drawer after a brief delay to ensure state updates
    setTimeout(() => {
      onReset?.()
    }, 0)
  }

  return (
    <div className="space-y-6">
      {/* Search Filter */}
      <div className="space-y-2">
        <Label htmlFor="search">{t("components.expenses.filters.search")}</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            id="search"
            type="text"
            placeholder={t("components.expenses.filters.searchPlaceholder")}
            value={localSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleSearchChange(e.target.value)
            }
            className="w-full pl-10 pr-8"
          />
          {localSearch && (
            <button
              type="button"
              onClick={() => handleSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t("components.expenses.filters.categories")}</Label>
          {categories.length > 0 && (
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={
                  allCategoriesSelected ? handleDeselectAllCategories : handleSelectAllCategories
                }
              >
                {allCategoriesSelected
                  ? t("components.expenses.filters.deselectAll")
                  : t("components.expenses.filters.selectAll")}
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {categories.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("components.expenses.filters.noCategories")}
            </p>
          ) : (
            categories.map((category) => {
              const isChecked = localFilters.categoryIds?.includes(category.id) || false
              return (
                <div
                  key={category.id}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors"
                  onClick={() => handleCategoryToggle(category.id)}
                >
                  <Checkbox
                    id={`category-${category.id}`}
                    checked={isChecked}
                    onCheckedChange={() => handleCategoryToggle(category.id)}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                      className="w-4 h-4 rounded flex-shrink-0"
                      style={getCategoryBackgroundStyle(category.color, category.id)}
                    />
                    <Label
                      htmlFor={`category-${category.id}`}
                      className="text-sm font-normal cursor-pointer flex-1 truncate"
                    >
                      {category.name}
                    </Label>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Recurring Filter */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{t("components.expenses.filters.recurring")}</Label>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={allRecurringSelected ? handleDeselectAllRecurring : handleSelectAllRecurring}
            >
              {allRecurringSelected
                ? t("components.expenses.filters.deselectAll")
                : t("components.expenses.filters.selectAll")}
            </Button>
          </div>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {(["one-time", "monthly", "quarterly", "yearly"] as const).map((recurringType) => {
            const isChecked = localFilters.recurringTypes?.includes(recurringType) || false
            const label = recurringType.charAt(0).toUpperCase() + recurringType.slice(1)
            return (
              <div
                key={recurringType}
                className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded-md transition-colors"
                onClick={() => handleRecurringToggle(recurringType)}
              >
                <Checkbox
                  id={`recurring-${recurringType}`}
                  checked={isChecked}
                  onCheckedChange={() => handleRecurringToggle(recurringType)}
                />
                <Label
                  htmlFor={`recurring-${recurringType}`}
                  className="text-sm font-normal cursor-pointer flex-1"
                >
                  {label}
                </Label>
              </div>
            )
          })}
        </div>
      </div>

      {/* Virtual Filter */}
      <div className="space-y-2">
        <Label>{t("components.expenses.filters.virtual")}</Label>
        <Select
          value={
            localFilters.isVirtual === undefined ? "all" : localFilters.isVirtual ? "true" : "false"
          }
          onValueChange={handleIsVirtualChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("components.expenses.filters.all")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("components.expenses.filters.all")}</SelectItem>
            <SelectItem value="true">{t("components.expenses.filters.virtualOnly")}</SelectItem>
            <SelectItem value="false">{t("components.expenses.filters.nonVirtualOnly")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-2 border-t">
        <Button variant="outline" onClick={handleReset} className="flex-1" size="sm">
          {t("components.expenses.filters.reset")}
        </Button>
        <Button onClick={handleApply} className="flex-1" size="sm">
          {t("components.expenses.filters.apply")}
        </Button>
      </div>
    </div>
  )
}

export default ExpenseFilters
